import { areIntervalsOverlapping, endOfDay, isAfter, isWithinInterval } from 'date-fns';
import { Action } from 'redux';
import { EMPTY, from, of } from 'rxjs';
import { filter, map, mergeMap, withLatestFrom } from 'rxjs/operators';
import {
    agreementUpdated,
    deleteAgreement,
    fetchReservedPrice,
    saveAgreementPrice
} from '../../../features/agreement/redux/agreementSlices';
import { dayAmount } from '../../../features/baseprice/basePriceTypes';
import { fetchPricePlans, updatePricePlan } from '../../../features/baseprice/redux/basePriceSlices';
import {
    fetchExternalPricelistPeriods,
    fetchExternalPrices,
    fetchExternalPricesByIds,
    fetchExternalPriceSource,
    fetchExternalPriceSourcesPartial,
    fetchExternalPricesPartial,
    saveExternalPriceComment
} from '../../../features/externalprice/redux/externalPriceSlices';
import {
    createExternalPricelist,
    removeExternalPricelist,
    updateExternalPricelist
} from '../../../features/externalpricelist/redux/externalPricelistSlices';
import {
    fetchInactivatePricesPartial,
    inactivatePricesTableFetchPricelistPeriodValidFroms
} from '../../../features/inactivateprices/redux/inactivatePricesSlices';
import { fetchPrices, fetchPricesPartial, savePriceComment } from '../../../features/price/redux/priceSlices';
import {
    createPricelist,
    deletePricelist,
    updatePricelist
} from '../../../features/pricelist/redux/pricelistSlices';
import { sendPricelistsToCustomers } from '../../../features/settings/redux/settingsSlices';
import { getAllPricelistChildren, hasParent, hasPermission, notNull, toDateString } from '../../../utils';
import {
    AgreementDto,
    AgreementPriceDto,
    ExternalPricelistDto,
    PricelistDto,
    PricePlanDto,
    ResourceType
} from '../../PricePrgClient';
import { hubListener, hubNotifier, signalROptions } from '../helpers';
import {
    notifyAgreementDeleted,
    notifyAgreementPriceDeleted,
    notifyAgreementPriceSaved,
    notifyAgreementSaved,
    notifyExternalPricelistCreated,
    notifyExternalPricelistDeleted,
    notifyExternalPricelistUpdated,
    notifyExternalPriceUpdated,
    notifyPriceActivated,
    notifyPriceCommentUpdated,
    notifyPricelistCreated,
    notifyPricelistDeleted,
    notifyPricelistUpdated,
    notifyPricePlansUpdated,
    notifyPricesRecalculated
} from '../signalrActions';
import {
    AgreementPricesUpdatedNotification,
    CommentType,
    CommentUpdatedNotification,
    PriceActivatedNotification,
    SignalrHub,
    SignalrPriceNotification
} from '../signalrTypes';

export const priceHub: SignalrHub = {
    hubName: 'price hub',
    url: '/signalr/priceHub',
    autoReconnect: true,
    options: signalROptions
};

export const priceHub$ = hubListener(priceHub, (hub, state$) => {
    const pricePlansUpdated$ = hub
        .on<{ pricelistId: number; productId: number; plans: PricePlanDto[] }>('PricePlansUpdated')
        .pipe(
            withLatestFrom(state$),
            filter(([payload, state]) => payload.pricelistId === state.basePrice.ui.selectedPricelistId),
            map(([payload]) => {
                const updatedProductIds = payload.plans.map(x => x.productId);

                return updatePricePlan.success({
                    updated: payload.plans,
                    deleted: [payload.productId].filter(id => !updatedProductIds.includes(id))
                });
            })
        );

    const priceUpdated$ = hub.on<SignalrPriceNotification>('PriceUpdated').pipe(
        withLatestFrom(
            state$.pipe(map(state => state.price.selectedDate)),
            state$.pipe(map(state => state.price.selectedPricelistIds)),
            state$.pipe(map(state => state.externalPricelist.externalPricelists)),
            state$.pipe(map(state => state.externalPrice)),
            state$.pipe(map(state => state.inactivatePrices.prices))
        ),
        mergeMap(
            ([
                payload,
                selectedDate,
                selectedPricelistIds,
                extPricelists,
                { selectedExternalPricelistIds, selectedExternalPriceDate, externalPrices },
                inactivatePricesData
            ]) => {
                const actions: Action[] = [];

                const { productId, pricelistId, validFrom } = payload;
                const validFromDate = new Date(toDateString(validFrom));

                if (
                    !isAfter(validFromDate, new Date(selectedDate)) &&
                    selectedPricelistIds.includes(pricelistId)
                ) {
                    actions.push(
                        fetchPricesPartial.request({
                            pricelistIds: [pricelistId],
                            validAt: selectedDate,
                            productIds: [productId]
                        })
                    );
                }

                if (
                    selectedExternalPriceDate != null &&
                    !isAfter(validFromDate, new Date(selectedExternalPriceDate))
                ) {
                    const affectedExtPricelistIds = selectedExternalPricelistIds.filter(
                        extPlId => extPricelists[extPlId]?.pricelistId === pricelistId
                    );

                    if (affectedExtPricelistIds.length > 0) {
                        actions.push(
                            fetchExternalPriceSourcesPartial.request({
                                extPricelistIds: affectedExtPricelistIds,
                                productId: productId,
                                validFrom: selectedExternalPriceDate
                            })
                        );

                        const externalPriceArr = Object.values(externalPrices);
                        const extPricelistIdsWithNewExtPrices = selectedExternalPricelistIds.filter(eplId =>
                            externalPriceArr
                                .filter(ep => ep.externalPricelistId === eplId)
                                .every(ep => ep.productId !== productId)
                        );

                        if (extPricelistIdsWithNewExtPrices.length > 0) {
                            actions.push(
                                fetchExternalPricesPartial.request({
                                    externalPricelistIds: extPricelistIdsWithNewExtPrices,
                                    productIds: [productId],
                                    validFrom: selectedExternalPriceDate
                                })
                            );
                        }
                    }
                }

                if (
                    inactivatePricesData.selectedPricelistDate != null &&
                    !isAfter(validFromDate, new Date(inactivatePricesData.selectedPricelistDate)) &&
                    inactivatePricesData.selectedPricelistIds.includes(pricelistId)
                ) {
                    if (inactivatePricesData.selectedPricelistIds.length > 0) {
                        actions.push(
                            fetchInactivatePricesPartial.request({
                                pricelistIds: inactivatePricesData.selectedPricelistIds,
                                productIds: [productId],
                                validFrom: inactivatePricesData.selectedPricelistDate
                            })
                        );
                    }
                }

                // Refetch pricelist periods if a pricelist period has been created.
                if (payload.pricelistPeriodCreated) {
                    if (selectedExternalPricelistIds.length > 0) {
                        actions.push(
                            fetchExternalPricelistPeriods.request({
                                extPricelistIds: selectedExternalPricelistIds,
                                preselectDate: false
                            })
                        );
                    }

                    if (inactivatePricesData.selectedPricelistIds.length > 0) {
                        actions.push(
                            inactivatePricesTableFetchPricelistPeriodValidFroms.request({
                                pricelistIds: inactivatePricesData.selectedPricelistIds,
                                preselectDate: false
                            })
                        );
                    }
                }

                return from(actions);
            }
        ),
        filter(notNull)
    );

    const priceUpdatedForChildren$ = hub.on<SignalrPriceNotification>('PriceUpdatedForChildren').pipe(
        withLatestFrom(
            state$.pipe(map(state => state.price.selectedDate)),
            state$.pipe(map(state => state.price.selectedPricelistIds)),
            state$.pipe(map(state => state.pricelist.pricelists)),
            state$.pipe(map(state => state.externalPricelist.externalPricelists)),
            state$.pipe(map(state => state.externalPrice)),
            state$.pipe(map(state => state.inactivatePrices.prices))
        ),
        mergeMap(
            ([
                payload,
                selectedDate,
                selectedPricelistIds,
                pricelists,
                externalPricelists,
                { selectedExternalPriceDate, selectedExternalPricelistIds, externalPrices },
                inactivatePricesData
            ]) => {
                const actions: Action[] = [];

                // TODO: Redan här skicka ut avgöra om vi behöver hämta ext prices på något sätt
                const { productId, pricelistId, validFrom } = payload;
                const validFromDate = new Date(toDateString(validFrom));

                if (!isAfter(validFromDate, new Date(selectedDate))) {
                    const plIdsWIthNewProductPrice = selectedPricelistIds.filter(plId =>
                        hasParent(plId, pricelistId, pricelists)
                    );

                    if (plIdsWIthNewProductPrice.length > 0) {
                        if (payload.pricelistPeriodCreated) {
                            actions.push(
                                fetchPrices.request({
                                    pricelistIds: selectedPricelistIds,
                                    validAt: validFrom
                                })
                            );
                        } else {
                            actions.push(
                                fetchPricesPartial.request({
                                    pricelistIds: plIdsWIthNewProductPrice,
                                    validAt: validFrom,
                                    productIds: [productId]
                                })
                            );
                        }
                    }
                }

                if (
                    selectedExternalPriceDate != null &&
                    !isAfter(validFromDate, new Date(selectedExternalPriceDate))
                ) {
                    const connectedPricelistIds = selectedExternalPricelistIds
                        .map(eplId => [eplId, externalPricelists[eplId]?.pricelistId] as const)
                        .filter(([, plId]) => plId != null);

                    const extPlIdsWithNewExtPrices = connectedPricelistIds
                        .filter(([, plId]) => hasParent(plId!, pricelistId, pricelists))
                        .map(([eplId]) => eplId);

                    if (extPlIdsWithNewExtPrices.length > 0) {
                        actions.push(
                            fetchExternalPriceSourcesPartial.request({
                                extPricelistIds: extPlIdsWithNewExtPrices,
                                productId: productId,
                                validFrom: selectedExternalPriceDate
                            })
                        );

                        const externalPriceArr = Object.values(externalPrices);
                        const extPricelistIdsWithNewExtPrices = selectedExternalPricelistIds.filter(eplId =>
                            externalPriceArr
                                .filter(ep => ep.externalPricelistId === eplId)
                                .every(ep => ep.productId !== productId)
                        );

                        if (extPricelistIdsWithNewExtPrices.length > 0) {
                            actions.push(
                                fetchExternalPricesPartial.request({
                                    externalPricelistIds: extPricelistIdsWithNewExtPrices,
                                    productIds: [productId],
                                    validFrom: selectedExternalPriceDate
                                })
                            );
                        }
                    }
                }

                if (
                    inactivatePricesData.selectedPricelistDate != null &&
                    !isAfter(validFromDate, new Date(inactivatePricesData.selectedPricelistDate)) &&
                    inactivatePricesData.selectedPricelistIds.length > 0
                ) {
                    const plIdsWIthNewProductPrice = inactivatePricesData.selectedPricelistIds.filter(plId =>
                        hasParent(plId, pricelistId, pricelists)
                    );

                    if (plIdsWIthNewProductPrice.length > 0) {
                        actions.push(
                            fetchInactivatePricesPartial.request({
                                pricelistIds: plIdsWIthNewProductPrice,
                                productIds: [productId],
                                validFrom: inactivatePricesData.selectedPricelistDate
                            })
                        );
                    }
                }

                // Refetch pricelist periods if a pricelist period has been created.
                if (payload.pricelistPeriodCreated) {
                    if (selectedExternalPricelistIds.length > 0) {
                        actions.push(
                            fetchExternalPricelistPeriods.request({
                                extPricelistIds: selectedExternalPricelistIds,
                                preselectDate: false
                            })
                        );
                    }

                    if (inactivatePricesData.selectedPricelistIds.length > 0) {
                        actions.push(
                            inactivatePricesTableFetchPricelistPeriodValidFroms.request({
                                pricelistIds: inactivatePricesData.selectedPricelistIds,
                                preselectDate: false
                            })
                        );
                    }
                }

                return from(actions);
            }
        ),
        filter(notNull)
    );

    const pricelistCreated$ = hub.on<PricelistDto>('PricelistCreated').pipe(map(createPricelist.success));
    const pricelistUpdated$ = hub.on<PricelistDto>('PricelistUpdated').pipe(map(updatePricelist.success));
    const pricelistDeleted$ = hub.on<number>('PricelistDeleted').pipe(map(deletePricelist.success));

    // If prices has been recalculated on pricelists which are selected in the price/pricePlan tables
    // then those prices will have to be refetched.
    const pricesRecalculated$ = hub.on<number>('PricesRecalculated').pipe(
        withLatestFrom(
            state$.pipe(map(state => state.price.selectedPricelistIds)),
            state$.pipe(map(state => state.pricelist.pricelists)),
            state$.pipe(map(state => state.price.selectedDate)),
            state$.pipe(map(state => state.basePrice.ui.selectedPricelistId)),
            state$.pipe(map(state => state.externalPrice))
        ),
        mergeMap(
            ([
                pricelistId,
                selectedPricelistIds,
                pricelists,
                selectedDate,
                selectedBasePricelistId,
                { selectedExternalPricelistIds, selectedExternalPriceDate, externalPriceSource }
            ]) => {
                const actions: Action[] = [];
                if (
                    selectedPricelistIds.includes(pricelistId) ||
                    selectedPricelistIds.some(plId => hasParent(plId, pricelistId, pricelists))
                ) {
                    actions.push(
                        fetchPrices.request({
                            pricelistIds: selectedPricelistIds,
                            validAt: selectedDate
                        })
                    );
                }

                if (
                    selectedBasePricelistId != null &&
                    selectedPricelistIds.includes(selectedBasePricelistId)
                ) {
                    actions.push(
                        fetchPricePlans.request({
                            pricelistId: selectedBasePricelistId,
                            dayAmount: dayAmount
                        })
                    );
                }

                if (selectedExternalPricelistIds.length > 0 && selectedExternalPriceDate != null) {
                    const extPriceSourceIds = Object.keys(externalPriceSource).map(Number);
                    if (extPriceSourceIds.length > 0) {
                        actions.push(fetchExternalPriceSource.request(extPriceSourceIds));
                    }

                    actions.push(
                        fetchExternalPrices.request({
                            date: selectedExternalPriceDate,
                            pricelistIds: selectedExternalPricelistIds
                        })
                    );
                }

                return from(actions);
            }
        )
    );

    const externalPriceUpdated$ = hub
        .on<number[]>('ExternalPriceUpdated')
        .pipe(map(fetchExternalPricesByIds.request));

    const externalPricelistCreated$ = hub
        .on<ExternalPricelistDto>('ExternalPricelistCreated')
        .pipe(map(createExternalPricelist.success));

    const externalPricelistDeleted$ = hub
        .on<number>('ExternalPricelistDeleted')
        .pipe(map(removeExternalPricelist.success));

    const priceCommentUpdated$ = hub.on<CommentUpdatedNotification>('PriceCommentUpdated').pipe(
        withLatestFrom(
            state$.pipe(map(state => state.pricelist.pricelists)),
            state$.pipe(map(state => state.price.prices))
        ),
        mergeMap(([payload, pricelists, prices]) => {
            const { comment, commentType, priceIds, pricelistId, productId } = payload;
            if (commentType === CommentType.InPrice) {
                if (!priceIds.some(pId => pId === prices[`${productId}-${pricelistId}`]?.priceId)) {
                    return EMPTY;
                }

                // Here is a minor issue here. If the user doesn't have permission
                // to read this pricelist (then it may not be available in the redux store),
                // but have permission to read its children, the children will not
                // be updated with the new comment. This logic should probably
                // be done on the server side.
                if (pricelists[pricelistId]?.applyCommentsToChildren) {
                    priceIds.push(
                        ...getAllPricelistChildren(pricelistId, Object.values(pricelists))
                            .map(plId => prices[`${productId}-${plId}`]?.priceId)
                            .filter(notNull)
                    );
                }

                return of(savePriceComment.success({ comment, priceIds }));
            }

            if (commentType === CommentType.ExtPrice) {
                return of(saveExternalPriceComment.success({ comment, extPriceIds: priceIds }));
            }

            return EMPTY;
        })
    );

    const agreementPriceSaved$ = hub.on<AgreementPriceDto>('AgreementPriceSaved').pipe(
        withLatestFrom(
            state$.pipe(map(state => state.user.currentUserPermissions)),
            state$.pipe(map(state => state.agreement.ui.agreementId)),
            state$.pipe(map(state => state.agreement.agreements))
        ),
        filter(([, cup]) => hasPermission(cup, ResourceType.AgreementPage)),
        mergeMap(([price, , selectedAgreementId, agreements]) => {
            if (selectedAgreementId == null) {
                return EMPTY;
            }

            if (selectedAgreementId === price.agreementId) {
                return of(saveAgreementPrice.success(price));
            }

            const selectedAgreement = agreements[selectedAgreementId];
            const updatedAgreement = agreements[price.agreementId];

            if (selectedAgreement == null || updatedAgreement == null) {
                return EMPTY;
            }

            // If intervals are overlapping this price may not be available to set anymore for
            // the selected agreement.
            if (
                areIntervalsOverlapping(
                    {
                        start: new Date(selectedAgreement.validFrom),
                        end: new Date(selectedAgreement.validTo)
                    },
                    { start: new Date(updatedAgreement.validFrom), end: new Date(updatedAgreement.validTo) },
                    { inclusive: true }
                )
            ) {
                return of(
                    fetchReservedPrice.request({
                        agreementId: selectedAgreementId,
                        productId: price.productId
                    })
                );
            }

            return EMPTY;
        })
    );

    const agreementSaved$ = hub.on<AgreementDto>('AgreementSaved').pipe(
        withLatestFrom(state$.pipe(map(state => state.user.currentUserPermissions))),
        map(([agreement, cup]) => {
            const agreementUserGroupIds = agreement.permittedUserGroups.map(x => x.userGroupId);

            if (
                agreementUserGroupIds.length === 0 ||
                cup.isAdmin ||
                cup.userGroups.some(ug => agreementUserGroupIds.includes(ug.userGroupId))
            ) {
                return agreementUpdated(agreement);
            }

            return deleteAgreement.success(agreement.agreementId);
        })
    );

    const agreementDeleted$ = hub.on<number>('AgreementDeleted').pipe(map(deleteAgreement.success));

    const pricelistMailsSentToCustomers = hub
        .on<string>('PricelistMailSendingCompleted')
        .pipe(
            map(errorMessage =>
                errorMessage
                    ? sendPricelistsToCustomers.failure(errorMessage)
                    : sendPricelistsToCustomers.success()
            )
        );

    const agreementPricesUpdated$ = hub.on<AgreementPricesUpdatedNotification>('AgreementPricesUpdated').pipe(
        withLatestFrom(
            state$.pipe(map(state => state.price.selectedDate)),
            state$.pipe(map(state => state.price.selectedPricelistIds)),
            state$.pipe(map(state => state.externalPrice.selectedExternalPriceDate)),
            state$.pipe(map(state => state.externalPrice.selectedExternalPricelistIds)),
            state$.pipe(map(state => state.externalPricelist.externalPricelists)),
            state$.pipe(map(state => state.inactivatePrices.prices))
        ),
        mergeMap(([payload, plDate, plIds, extPlDate, extPlIds, extPls, inactivatePrices]) => {
            const actions: Action[] = [];

            if (payload.pricelistIds.length === 0 || payload.productIds.length === 0) {
                return [];
            }

            if (
                payload.pricelistIds.some(plId => plIds.includes(plId)) &&
                isWithinInterval(new Date(plDate), {
                    start: new Date(payload.validFrom),
                    end: endOfDay(new Date(payload.validTo))
                })
            ) {
                actions.push(
                    fetchPricesPartial.request({
                        pricelistIds: plIds,
                        productIds: payload.productIds,
                        validAt: plDate
                    })
                );
            }

            const extPlPricelistIds = extPlIds.map(extPlId => extPls[extPlId]?.pricelistId).filter(notNull);

            if (
                payload.pricelistIds.some(plId => extPlPricelistIds.includes(plId)) &&
                extPlDate != null &&
                isWithinInterval(new Date(extPlDate), {
                    start: new Date(payload.validFrom),
                    end: endOfDay(new Date(payload.validTo))
                })
            ) {
                actions.push(
                    fetchExternalPricesPartial.request({
                        externalPricelistIds: extPlIds,
                        validFrom: extPlDate,
                        productIds: payload.productIds
                    })
                );
            }

            if (
                payload.pricelistIds.some(plId => inactivatePrices.selectedPricelistIds.includes(plId)) &&
                inactivatePrices.selectedPricelistDate != null &&
                isWithinInterval(new Date(inactivatePrices.selectedPricelistDate), {
                    start: new Date(payload.validFrom),
                    end: new Date(payload.validTo)
                })
            ) {
                actions.push(
                    fetchInactivatePricesPartial.request({
                        pricelistIds: inactivatePrices.selectedPricelistIds,
                        validFrom: inactivatePrices.selectedPricelistDate,
                        productIds: payload.productIds
                    })
                );
            }
            return actions;
        })
    );

    const externalPricelistUpdated$ = hub
        .on<ExternalPricelistDto>('ExternalPricelistUpdated')
        .pipe(map(updateExternalPricelist.success));

    const priceActivated$ = hub.on<PriceActivatedNotification>('PriceActivated').pipe(
        withLatestFrom(
            state$.pipe(map(state => state.pricelist.pricelists)),
            state$.pipe(map(state => state.inactivatePrices.prices))
        ),
        filter(
            ([payload, pricelists, priceData]) =>
                priceData.selectedPricelistDate != null &&
                !isAfter(new Date(payload.validFrom), new Date(priceData.selectedPricelistDate)) &&
                priceData.selectedPricelistIds.some(
                    plId => plId === payload.pricelistId || hasParent(plId, payload.pricelistId, pricelists)
                )
        ),
        map(([payload, , priceData]) =>
            fetchInactivatePricesPartial.request({
                pricelistIds: priceData.selectedPricelistIds,
                productIds: [payload.productId],
                validFrom: priceData.selectedPricelistDate!
            })
        )
    );

    return [
        pricePlansUpdated$,
        priceUpdated$,
        priceUpdatedForChildren$,
        pricelistCreated$,
        pricelistUpdated$,
        pricelistDeleted$,
        pricesRecalculated$,
        externalPriceUpdated$,
        externalPricelistCreated$,
        externalPricelistDeleted$,
        priceCommentUpdated$,
        agreementPriceSaved$,
        agreementSaved$,
        agreementDeleted$,
        pricelistMailsSentToCustomers,
        agreementPricesUpdated$,
        externalPricelistUpdated$,
        priceActivated$
    ];
});

const notifyPricelistCreated$ = hubNotifier(priceHub, notifyPricelistCreated, (hub, payload) =>
    hub.send('NotifyPricelistCreated', payload)
);

const notifyPricelistUpdated$ = hubNotifier(priceHub, notifyPricelistUpdated, (hub, payload) =>
    hub.send('NotifyPricelistUpdated', payload)
);

const notifyPricelistDeleted$ = hubNotifier(priceHub, notifyPricelistDeleted, (hub, payload) =>
    hub.send('NotifyPricelistDeleted', payload)
);

const notifyPricesRecalculated$ = hubNotifier(priceHub, notifyPricesRecalculated, (hub, payload) =>
    hub.send('NotifyPricesRecalculated', payload)
);

const notifyExternalPriceUpdated$ = hubNotifier(priceHub, notifyExternalPriceUpdated, (hub, payload) =>
    hub.send('NotifyExternalPriceUpdated', payload)
);

const notifyExternalPricelistCreated$ = hubNotifier(
    priceHub,
    notifyExternalPricelistCreated,
    (hub, payload) => hub.send('NotifyExternalPricelistCreated', payload)
);

const notifyExternalPricelistDeleted$ = hubNotifier(
    priceHub,
    notifyExternalPricelistDeleted,
    (hub, payload) => hub.send('NotifyExternalPricelistDeleted', payload)
);

const notifyPricePlansUpdated$ = hubNotifier(priceHub, notifyPricePlansUpdated, (hub, payload) =>
    hub.send('NotifyPricePlansUpdated', payload.pricelistId, payload.productId, dayAmount)
);

const notifyPriceCommentUpdated$ = hubNotifier(priceHub, notifyPriceCommentUpdated, (hub, payload) =>
    hub.send('NotifyPriceCommentUpdated', payload)
);

const notifyAgreementPriceSaved$ = hubNotifier(priceHub, notifyAgreementPriceSaved, (hub, payload) =>
    hub.send('NotifyAgreementPriceSaved', payload)
);

const notifyAgreementPriceDeleted$ = hubNotifier(priceHub, notifyAgreementPriceDeleted, (hub, payload) =>
    hub.send('NotifyAgreementPriceDeleted', payload.agreementPriceId, payload.agreementId, payload.productId)
);

const notifyAgreementSaved$ = hubNotifier(priceHub, notifyAgreementSaved, (hub, payload) =>
    hub.send('NotifyAgreementSaved', payload)
);

const notifyAgreementDeleted$ = hubNotifier(priceHub, notifyAgreementDeleted, (hub, payload) =>
    hub.send('NotifyAgreementDeleted', payload)
);

const notifyExternalPricelistUpdated$ = hubNotifier(
    priceHub,
    notifyExternalPricelistUpdated,
    (hub, payload) => hub.send('NotifyExternalPricelistUpdated', payload)
);

const notifyPriceActivated$ = hubNotifier(priceHub, notifyPriceActivated, (hub, payload) =>
    hub.send('NotifyPriceActivated', payload)
);

export default [
    priceHub$,
    notifyPricePlansUpdated$,
    notifyPricelistCreated$,
    notifyPricelistUpdated$,
    notifyPricelistDeleted$,
    notifyPricesRecalculated$,
    notifyExternalPriceUpdated$,
    notifyExternalPricelistCreated$,
    notifyExternalPricelistDeleted$,
    notifyPriceCommentUpdated$,
    notifyAgreementPriceSaved$,
    notifyAgreementSaved$,
    notifyAgreementDeleted$,
    notifyAgreementPriceDeleted$,
    notifyExternalPricelistUpdated$,
    notifyPriceActivated$
];
